import { ObstacleType } from '../../obstacles/ObstacleType';
import { doubleJumpStepReach, takeOffWindow, type StepReach } from '../../physics/JumpReach';
import type { CeilingZone, SegmentDefinition, SegmentTerrain } from '../segments/SegmentDefinition';
import { combineLaneTypes } from '../validation/SequenceValidator';
import type { LayoutRules } from './LayoutRules';
import { EMPTY_TERRAIN, highestGroundIn, lowestCeilingIn } from './terrainQueries';

/**
 * Terrain seen through the eyes of the sequence validator: every pit, step and
 * low ceiling becomes a decision row (lane → action needed), exactly like an
 * obstacle row. Stretches with walls between the lanes (walk-in pipes, the sides
 * of a raised platform) block lane changes: no lane-change obstacles inside, and
 * lane changes for the next row only count from the stretch's end
 * (SequenceValidator.wallsEnd).
 *
 * A runner on top of raised ground crosses short gaps to ground of the same height
 * (containers in a row) without a jump – no row there (LayoutRules.maxRunAcrossGap).
 * Steps that need a double jump (container roofs) must leave fair timing at start and
 * top speed with the flat run-up inside the segment (physics/JumpReach.ts).
 *
 * Walkable tops over open ground (slabs: the crown of a walk-in pipe) are decks – an optional
 * route beside the ground route, so no row. Getting up must be as fair as a double-jump step,
 * and the drop off the end must land on plain road with the lane free until the runner reacted.
 *
 * Low overhead elements with a walkable top (pipe bridges, beams, crossbars – underside below the
 * standing height) are crossings: their row stays a slide (the guaranteed way past), and because
 * their collider ends where the model visibly ends, a (double) jump that clears the top gets over
 * them or onto them (docs/level-design/SEGMENT_STANDARD.md "Sprungehrlichkeit"). No route along them:
 * no climb or drop-zone guarantee – the jump-over lines are proven by tests/unit/jumpHonesty.test.ts.
 */
export interface TerrainRow {
  /** Segment-local z of the decision line. */
  readonly z: number;
  /** Action per lane (null = nothing to do). */
  readonly lanes: readonly (ObstacleType | null)[];
}

export interface WallStretch {
  readonly zFrom: number;
  readonly zTo: number;
}

/** A step up that needs a double jump (e.g. a container roof) and how it can be reached. */
export interface TerrainClimb {
  /** Segment-local z of the step face. */
  readonly z: number;
  readonly lane: number;
  /** Height of the step above the take-off ground (m). */
  readonly rise: number;
  /** Flat ground before the face inside the segment, front of the runner → face (m). */
  readonly runUp: number;
  /**
   * Free ground the fairness check relies on (runner centre → face, at top speed, meters): no
   * obstacle may stand in this stretch of the lane.
   */
  readonly runUpNeeded: number;
  /** Timing tolerance of the double jump (speed independent). */
  readonly reach: StepReach;
}

/**
 * A walkable top over open ground in one lane (the crown of a walk-in pipe): the runner gets up with a
 * (double) jump, runs along it and drops back to the road at its end. Optional – the ground route runs
 * through or beside it, the sequence validator never relies on it.
 */
export interface TerrainDeck {
  readonly lane: number;
  /** Segment-local z of the face (where the runner gets up) and of the end (where it drops off). */
  readonly zFrom: number;
  readonly zTo: number;
  /** Height of the top (segment-local y). */
  readonly height: number;
  /** The double jump up from the ground before the face, or null where a single jump gets up. */
  readonly climb: TerrainClimb | null;
  /**
   * Free lane needed after the end (m): the fall at top speed plus one reaction time – no obstacle
   * may stand there, no step or low ceiling either.
   */
  readonly dropZone: number;
}

/**
 * A low overhead element with a walkable top over one lane (pipe bridge, precast beam, crossbar): slid
 * under (its row) or jumped over / landed on – the top is where the model visibly ends.
 */
export interface TerrainCrossing {
  readonly id: string;
  readonly lane: number;
  readonly zFrom: number;
  readonly zTo: number;
  /** Lowest hard underside over the lane there (segment-local y) – below the standing height. */
  readonly underside: number;
  /** Walkable top (segment-local y). */
  readonly top: number;
}

export interface TerrainAnalysis {
  readonly rows: readonly TerrainRow[];
  /** z ranges with walls between lanes – no lane changes there (no LANE_CHANGE obstacles either). */
  readonly wallStretches: readonly WallStretch[];
  /** Steps that need a double jump, with their run-up. */
  readonly climbs: readonly TerrainClimb[];
  /** Walkable tops over open ground (optional routes) with their climb and drop zone. */
  readonly decks: readonly TerrainDeck[];
  /** Low overhead elements with a walkable top: slide row, honest jump-over (no route). */
  readonly crossings: readonly TerrainCrossing[];
  /** Layout problems (validator errors). */
  readonly issues: readonly string[];
}

interface RowEvent {
  readonly z: number;
  readonly lane: number;
  readonly type: ObstacleType | null;
}

/** Edge of raised ground the runner walked off – it may cross a short gap to the same height. */
interface Ledge {
  readonly z: number;
  readonly height: number;
}

const EPSILON = 1e-6;
const EMPTY_ANALYSIS: TerrainAnalysis = { rows: [], wallStretches: [], climbs: [], decks: [], crossings: [], issues: [] };

export function analyzeTerrain(definition: SegmentDefinition, rules: LayoutRules): TerrainAnalysis {
  const terrain = definition.terrain ?? EMPTY_TERRAIN;
  if (terrain.surfaces.length + terrain.gaps.length + terrain.ceilings.length === 0) return EMPTY_ANALYSIS;

  const issues = new Set<string>();
  const events: RowEvent[] = [];
  const climbs: TerrainClimb[] = [];
  const base = rules.groundHeight;
  const length = definition.length;
  const step = rules.sampleStep;
  const half = rules.playerWidth / 2;
  const samples = Math.ceil(length / step - EPSILON);

  for (const lane of definition.lanes) {
    const minX = lane.centerX - half;
    const maxX = lane.centerX + half;
    const name = `lane ${lane.index}`;
    let rim = base;
    let previous: number | null = base;
    let gapStart: number | null = null;
    let ceilingType: ObstacleType | null = null;
    let ledge: Ledge | null = null;
    /** Start of the flat ground under the runner – the run-up of the next step. */
    let flatFrom = 0;

    for (let i = 0; i < samples; i++) {
      const z0 = i * step;
      const z1 = Math.min(length, z0 + step);
      const ground = highestGroundIn(terrain, base, z0, z1, minX, maxX);
      const atConnector = z0 < rules.connectorClearance - EPSILON || z1 > length - rules.connectorClearance + EPSILON;
      // a raised route that leads on into the next module (the tower column onto the shell's upper floor) may reach OUT
      const onward = z1 > length - rules.connectorClearance + EPSILON && terrain.surfaces.some((s) => s.leadsOnward && s.zTo >= length - EPSILON && s.xFrom <= maxX && s.xTo >= minX);
      if (atConnector && !onward && (ground === null || Math.abs(ground - base) > EPSILON)) {
        issues.add(`${name}: ground must be level within ${rules.connectorClearance} m of IN/OUT (segments connect at ground level)`);
      }

      // ── Pits and steps ──
      if (ground === null) {
        gapStart ??= z0;
      } else {
        if (gapStart !== null) {
          const gapLength = z0 - gapStart;
          if (gapLength > rules.maxJumpGap + EPSILON) {
            issues.add(
              `${name}: pit at z=${gapStart.toFixed(2)} is ${gapLength.toFixed(2)} m long – a single jump clears ${rules.maxJumpGap.toFixed(2)} m at start speed (split it with a platform)`,
            );
          }
          if (ground - rim > rules.jumpHeight - rules.maxStepHeight + EPSILON) {
            issues.add(`${name}: far side of the pit at z=${z0.toFixed(2)} is too high to land on`);
          }
          events.push({ z: gapStart, lane: lane.index, type: ObstacleType.Jump });
          gapStart = null;
        } else if (previous !== null && ground - previous > rules.maxStepHeight + EPSILON) {
          if (crossesGap(ledge, z0, ground, rules)) {
            ledge = null; // the runner on top ran across – nothing to do
          } else {
            const rise = ground - previous;
            const type = stepType(rise, rules);
            events.push({ z: z0, lane: lane.index, type });
            if (type === ObstacleType.DoubleJump) {
              const climb = checkClimb(z0, lane.index, rise, z0 - flatFrom - rules.playerDepth / 2, rules);
              climbs.push(climb.climb);
              for (const problem of climb.issues) issues.add(`${name}: ${problem}`);
            }
          }
        } else if (previous !== null && previous - ground > rules.maxStepHeight + EPSILON) {
          ledge = { z: z0, height: previous };
        }
        rim = ground;
      }
      if (ledge && z0 - ledge.z > rules.maxRunAcrossGap + EPSILON) ledge = null;
      if (ground === null || previous === null || Math.abs(ground - previous) > EPSILON) flatFrom = z0;
      previous = ground;

      // ── Ceilings ──
      const floor = ground ?? rim;
      const soft = lowestCeilingIn(terrain, z0, z1, minX, maxX, -Infinity, 'soft');
      if (soft !== null && soft - floor < rules.standingHeight - EPSILON) {
        issues.add(`${name}: soft ceiling at z=${z0.toFixed(2)} is lower than the standing height`);
      }
      const hard = lowestCeilingIn(terrain, z0, z1, minX, maxX, -Infinity, 'hard');
      const headroom = hard === null ? Infinity : hard - floor;
      const type = headroom < rules.slidingHeight - EPSILON ? ObstacleType.LaneChange : headroom < rules.standingHeight - EPSILON ? ObstacleType.Slide : null;
      if (type !== ceilingType && type !== null) events.push({ z: z0, lane: lane.index, type });
      ceilingType = type;
      if (atConnector && type !== null) issues.add(`${name}: no low ceiling within ${rules.connectorClearance} m of IN/OUT`);
      if (ground === null) {
        if (type === ObstacleType.Slide) issues.add(`${name}: slide ceiling over the pit at z=${z0.toFixed(2)}`);
        const over = Math.min(hard ?? Infinity, soft ?? Infinity);
        if (over - rim < rules.standingHeight + rules.jumpHeight - EPSILON) {
          issues.add(`${name}: ceiling over the pit at z=${z0.toFixed(2)} blocks the jump`);
        }
      }
    }
    if (gapStart !== null) issues.add(`${name}: pit reaches the end of the segment`);
  }

  const wallStretches = findWallStretches(definition, rules);
  const crossings = findCrossings(definition, rules);
  const decks = findDecks(definition, rules, issues);
  const rows = mergeEvents(events, definition.lanes.length, rules.rowMergeDistance);
  for (const row of rows) {
    if (!row.lanes.some((type) => type !== ObstacleType.LaneChange)) issues.add(`terrain row at z=${row.z.toFixed(2)} blocks every lane`);
  }
  return { rows, wallStretches, climbs, decks, crossings, issues: [...issues] };
}

/** Slabs that cover the running line of a lane end to end. */
function slabsOver(terrain: SegmentTerrain, minX: number, maxX: number): CeilingZone[] {
  // an upper floor is reached and left across the module borders – no crossing, no deck of this module
  return terrain.ceilings.filter((c) => c.top !== undefined && !c.upperFloor && c.xFrom <= minX + EPSILON && c.xTo >= maxX - EPSILON);
}

/**
 * Hard underside over a slab's stretch of a lane, measured from the ground there: below the standing height
 * the slab is a crossing (slid under or jumped over), otherwise the runner walks under it (deck over open ground).
 */
function headroomUnder(terrain: SegmentTerrain, base: number, slab: CeilingZone, minX: number, maxX: number): { readonly underside: number; readonly headroom: number } {
  const underside = lowestCeilingIn(terrain, slab.zFrom, slab.zTo, minX, maxX, -Infinity, 'hard') ?? slab.clearance;
  const floor = highestGroundIn(terrain, base, slab.zFrom, slab.zTo, minX, maxX) ?? base;
  return { underside, headroom: underside - floor };
}

function findCrossings(definition: SegmentDefinition, rules: LayoutRules): TerrainCrossing[] {
  const terrain = definition.terrain ?? EMPTY_TERRAIN;
  const half = rules.playerWidth / 2;
  const crossings: TerrainCrossing[] = [];
  for (const lane of definition.lanes) {
    const minX = lane.centerX - half;
    const maxX = lane.centerX + half;
    for (const slab of slabsOver(terrain, minX, maxX)) {
      const { underside, headroom } = headroomUnder(terrain, rules.groundHeight, slab, minX, maxX);
      if (headroom >= rules.standingHeight - EPSILON) continue;
      crossings.push({ id: slab.id, lane: lane.index, zFrom: slab.zFrom, zTo: slab.zTo, underside, top: slab.top ?? slab.clearance });
    }
  }
  return crossings.sort((a, b) => a.zFrom - b.zFrom || a.lane - b.lane);
}

/** Slabs covering a lane's running line end to end (gap ≤ maxRunAcrossGap, tops within a step): one deck. */
interface DeckSpan {
  readonly zFrom: number;
  readonly zTo: number;
  readonly height: number;
}

/**
 * Walkable tops over open ground per lane (TerrainDeck). Getting up needs the same fairness as a
 * double-jump step (checkClimb) with a flat run-up free of low ceilings; the drop off the end must land
 * inside the module (connector clearance, like the container row's drop) on plain road, and the lane
 * stays open until the runner has landed at top speed and reacted (dropZone).
 */
function findDecks(definition: SegmentDefinition, rules: LayoutRules, issues: Set<string>): TerrainDeck[] {
  const terrain = definition.terrain ?? EMPTY_TERRAIN;
  const base = rules.groundHeight;
  const half = rules.playerWidth / 2;
  const { maxSpeed, reactionTime } = rules.sequence;
  const decks: TerrainDeck[] = [];
  for (const lane of definition.lanes) {
    const minX = lane.centerX - half;
    const maxX = lane.centerX + half;
    // Crossings (a slide row under them) are no decks – see findCrossings.
    const slabs = slabsOver(terrain, minX, maxX).filter((slab) => headroomUnder(terrain, base, slab, minX, maxX).headroom >= rules.standingHeight - EPSILON);
    for (const span of deckSpans(slabs, rules)) {
      const where = `lane ${lane.index}: walkable top at z=${span.zFrom.toFixed(2)}`;
      const before = highestGroundIn(terrain, base, span.zFrom - rules.sampleStep, span.zFrom, minX, maxX) ?? base;
      const rise = span.height - before;
      const type = stepType(rise, rules);
      let climb: TerrainClimb | null = null;
      if (type === ObstacleType.LaneChange) issues.add(`${where} is out of reach (${rise.toFixed(2)} m up)`);
      if (type === ObstacleType.DoubleJump) {
        const checked = checkClimb(span.zFrom, lane.index, rise, openRunUp(terrain, base, span.zFrom, before, minX, maxX, rules), rules);
        climb = checked.climb;
        for (const problem of checked.issues) issues.add(`${where}: ${problem}`);
      }
      const after = highestGroundIn(terrain, base, span.zTo, span.zTo + rules.sampleStep, minX, maxX) ?? base;
      const fallTime = Math.sqrt((2 * Math.max(0, span.height - after)) / rules.physics.gravity);
      const landing = span.zTo + fallTime * maxSpeed;
      const dropZone = (fallTime + reactionTime) * maxSpeed;
      if (landing > definition.length - rules.connectorClearance + EPSILON) {
        issues.add(`${where}: the drop off its end lands at z=${landing.toFixed(2)} at ${maxSpeed} m/s – keep it ${rules.connectorClearance} m before OUT`);
      }
      const blocked = dropZoneProblem(terrain, base, span, after, Math.min(definition.length, span.zTo + dropZone), minX, maxX, rules);
      if (blocked) issues.add(`${where}: ${blocked} in the drop zone (${dropZone.toFixed(1)} m after its end)`);
      decks.push({ lane: lane.index, zFrom: span.zFrom, zTo: span.zTo, height: span.height, climb, dropZone });
    }
  }
  return decks;
}

function deckSpans(slabs: readonly CeilingZone[], rules: LayoutRules): DeckSpan[] {
  const spans: DeckSpan[] = [];
  for (const slab of [...slabs].sort((a, b) => a.zFrom - b.zFrom)) {
    const height = slab.top ?? slab.clearance;
    const last = spans[spans.length - 1];
    if (last && slab.zFrom - last.zTo <= rules.maxRunAcrossGap + EPSILON && Math.abs(height - last.height) <= rules.maxStepHeight + EPSILON) {
      spans[spans.length - 1] = { zFrom: last.zFrom, zTo: Math.max(last.zTo, slab.zTo), height: Math.max(last.height, height) };
    } else {
      spans.push({ zFrom: slab.zFrom, zTo: slab.zTo, height });
    }
  }
  return spans;
}

/** Flat, open ground right before `face` (front of the runner → face, m): same height, no hard ceiling over the lane. */
function openRunUp(terrain: SegmentTerrain, base: number, face: number, ground: number, minX: number, maxX: number, rules: LayoutRules): number {
  let from = face;
  while (from > EPSILON) {
    const z0 = Math.max(0, from - rules.sampleStep);
    const height = highestGroundIn(terrain, base, z0, from, minX, maxX);
    if (height === null || Math.abs(height - ground) > EPSILON) break;
    if (lowestCeilingIn(terrain, z0, from, minX, maxX, -Infinity, 'hard') !== null) break;
    from = z0;
  }
  return Math.max(0, face - from - rules.playerDepth / 2);
}

/** A step, pit or low ceiling (below the head of a runner on the top) between the end of a deck and `to` – or null. */
function dropZoneProblem(
  terrain: SegmentTerrain,
  base: number,
  span: DeckSpan,
  ground: number,
  to: number,
  minX: number,
  maxX: number,
  rules: LayoutRules,
): string | null {
  for (let z0 = span.zTo; z0 < to - EPSILON; z0 += rules.sampleStep) {
    const z1 = Math.min(to, z0 + rules.sampleStep);
    const height = highestGroundIn(terrain, base, z0, z1, minX, maxX);
    if (height === null || Math.abs(height - ground) > rules.maxStepHeight + EPSILON) return `a step or pit at z=${z0.toFixed(2)}`;
    const ceiling = lowestCeilingIn(terrain, z0, z1, minX, maxX, -Infinity, 'hard');
    if (ceiling !== null && ceiling < span.height + rules.standingHeight - EPSILON) return `a low ceiling at z=${z0.toFixed(2)}`;
  }
  return null;
}

/** The runner who walked off `ledge` reaches ground at z of about the same height before it dropped a step. */
function crossesGap(ledge: Ledge | null, z: number, ground: number, rules: LayoutRules): boolean {
  return ledge !== null && z - ledge.z <= rules.maxRunAcrossGap + EPSILON && Math.abs(ground - ledge.height) <= rules.maxStepHeight + EPSILON;
}

/**
 * A double-jump step must leave fair timing (LayoutRules.doubleJumpStep) at start and top speed:
 * input tolerance for the second jump, and a span of take-off moments within the flat run-up.
 */
function checkClimb(z: number, lane: number, rise: number, runUp: number, rules: LayoutRules): { climb: TerrainClimb; issues: string[] } {
  const { minTriggerWindow, minTakeOffWindow } = rules.doubleJumpStep;
  const reach = doubleJumpStepReach(rules.physics, rules.maxStepHeight, rise, minTriggerWindow, rules.simulationStep);
  const where = `the ${rise.toFixed(2)} m step at z=${z.toFixed(2)}`;
  const issues: string[] = [];
  const best = reach.bestTrigger ? reach.bestTrigger.to - reach.bestTrigger.from : 0;
  if (!reach.arrival) {
    issues.push(`${where} leaves ${ms(best)} for the double jump (needs ${ms(minTriggerWindow)})`);
  }
  const speeds = [rules.sequence.minSpeed, rules.sequence.maxSpeed];
  for (const speed of speeds) {
    const window = takeOffWindow(reach, runUp, speed);
    if (reach.arrival && window < minTakeOffWindow - EPSILON) {
      issues.push(`${where} leaves ${ms(window)} to take off for the double jump at ${speed} m/s (needs ${ms(minTakeOffWindow)} – longer flat run-up)`);
    }
  }
  const latest = reach.arrival ? reach.arrival.from + minTakeOffWindow : 0;
  const runUpNeeded = Math.max(...speeds.map((speed) => latest * speed)) + rules.playerDepth / 2;
  return { climb: { z, lane, rise, runUp, runUpNeeded, reach }, issues };
}

const ms = (seconds: number) => `${Math.round(seconds * 1000)} ms`;

/** Action needed to get up a step of the given height. */
function stepType(rise: number, rules: LayoutRules): ObstacleType {
  // The feet must clear the edge: a step up to the jump height (minus a step) is a jump.
  if (rise <= rules.jumpHeight - rules.maxStepHeight + EPSILON) return ObstacleType.Jump;
  if (rise <= rules.jumpHeight + rules.doubleJumpHeight - rules.maxStepHeight + EPSILON) return ObstacleType.DoubleJump;
  return ObstacleType.LaneChange;
}

/**
 * Stretches in which walls stand between lanes: full-height ceiling zones (pipe shells,
 * posts) that miss every lane corridor, and raised surfaces (their side faces).
 */
function findWallStretches(definition: SegmentDefinition, rules: LayoutRules): WallStretch[] {
  const terrain = definition.terrain ?? EMPTY_TERRAIN;
  const half = rules.playerWidth / 2;
  const stretches: WallStretch[] = [];
  for (const ceiling of terrain.ceilings) {
    if (ceiling.clearance - rules.groundHeight >= rules.slidingHeight - EPSILON) continue;
    const hitsLane = definition.lanes.some((lane) => ceiling.xTo > lane.centerX - half && ceiling.xFrom < lane.centerX + half);
    if (!hitsLane) stretches.push({ zFrom: ceiling.zFrom, zTo: ceiling.zTo });
  }
  for (const surface of terrain.surfaces) {
    const limit = rules.groundHeight + rules.maxStepHeight;
    const { heightFrom: h0, heightTo: h1, zFrom, zTo } = surface;
    if (Math.max(h0, h1) <= limit + EPSILON) continue;
    if (Math.abs(h1 - h0) < EPSILON) {
      stretches.push({ zFrom, zTo });
      continue;
    }
    // Where a ramp rises above a step, its sides become walls.
    const crossing = zFrom + ((limit - h0) / (h1 - h0)) * (zTo - zFrom);
    stretches.push(h1 > h0 ? { zFrom: Math.max(zFrom, crossing), zTo } : { zFrom, zTo: Math.min(zTo, crossing) });
  }
  return stretches.sort((a, b) => a.zFrom - b.zFrom);
}

function mergeEvents(events: readonly RowEvent[], laneCount: number, mergeDistance: number): TerrainRow[] {
  const sorted = [...events].sort((a, b) => a.z - b.z);
  const rows: { z: number; lanes: (ObstacleType | null)[] }[] = [];
  for (const event of sorted) {
    let row = rows[rows.length - 1];
    if (!row || event.z - row.z > mergeDistance + EPSILON) {
      row = { z: event.z, lanes: Array.from({ length: laneCount }, () => null) };
      rows.push(row);
    }
    row.lanes[event.lane] = combineLaneTypes(row.lanes[event.lane] ?? null, event.type);
  }
  return rows;
}
